import React, { useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, Alert } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { CameraView } from 'expo-camera';
import * as ImageManipulator from 'expo-image-manipulator';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { cameraStepStyles } from './cameraStepStyles';
import type { DualPhotoCaptureStackParamList } from './dualPhotoCaptureNavigation.types';
import { useDualPhotoCaptureCallbacks } from './DualPhotoCaptureContext';
import { useCameraShutterReady } from './useCameraShutterReady';

export type DualPhotoFrontScreenProps = NativeStackScreenProps<DualPhotoCaptureStackParamList, 'DualPhotoFront'>;

/**
 * Step 2 of 2: front-facing camera only.
 */
export default function DualPhotoFrontScreen({ navigation, route }: DualPhotoFrontScreenProps) {
  const { onDone, onCancel, onSkipWithoutPhotos, caption } = useDualPhotoCaptureCallbacks();
  const { backUri } = route.params;
  const cameraRef = useRef<CameraView>(null);
  const [flashEnabled, setFlashEnabled] = React.useState(false);
  const { isCameraReady, onCameraReady, onMountError, isSimulator } = useCameraShutterReady();
  const [showShutterEffect, setShowShutterEffect] = React.useState(false);
  const shutterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (shutterTimeoutRef.current) clearTimeout(shutterTimeoutRef.current);
    };
  }, []);

  const triggerShutterEffect = () => {
    if (shutterTimeoutRef.current) clearTimeout(shutterTimeoutRef.current);
    setShowShutterEffect(true);
    shutterTimeoutRef.current = setTimeout(() => {
      shutterTimeoutRef.current = null;
      setShowShutterEffect(false);
    }, 150);
  };

  const capturePhoto = async () => {
    if (!isSimulator && (!isCameraReady || !cameraRef.current)) {
      Alert.alert(
        'Camera not ready',
        'Please wait a moment for the front camera to load, then try again.'
      );
      return;
    }
    if (!cameraRef.current) {
      Alert.alert(
        'Camera unavailable',
        isSimulator
          ? 'The simulator has no camera. Use Skip to continue testing the check-in flow.'
          : 'Please wait a moment and try again.'
      );
      return;
    }
    triggerShutterEffect();
    try {
      const photo = await cameraRef.current.takePictureAsync({
        quality: 0.8,
        base64: false,
      });
      const normalizedPhoto = await ImageManipulator.manipulateAsync(
        photo.uri,
        [],
        { compress: 0.85, format: ImageManipulator.SaveFormat.JPEG }
      );
      setTimeout(() => {
        onDone(normalizedPhoto.uri, backUri, caption);
      }, 100);
    } catch (error) {
      console.error('Error capturing photo:', error);
      Alert.alert('Error', 'Failed to capture photo. Please try again.');
    }
  };

  const toggleFlash = () => setFlashEnabled((f) => !f);

  const retakeBack = () => {
    navigation.navigate('DualPhotoBack');
  };

  return (
    <SafeAreaView style={cameraStepStyles.container}>
      <CameraView
        ref={cameraRef}
        style={cameraStepStyles.camera}
        facing="front"
        mode="picture"
        flash={flashEnabled ? 'on' : 'off'}
        onCameraReady={onCameraReady}
        onMountError={onMountError}
      />
      <View style={cameraStepStyles.overlay} pointerEvents="box-none">
        <View style={cameraStepStyles.topBar}>
          <View style={cameraStepStyles.topBarRowWrap}>
            <View style={cameraStepStyles.topBarSides}>
              <TouchableOpacity
                style={[cameraStepStyles.cameraCircleButton, cameraStepStyles.topBarControlZ]}
                onPress={retakeBack}
                activeOpacity={0.85}
                accessibilityLabel="Retake back photo"
                accessibilityRole="button"
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="chevron-back" size={26} color="#fff" />
              </TouchableOpacity>
              <TouchableOpacity
                style={[cameraStepStyles.skipButton, cameraStepStyles.topBarControlZ]}
                onPress={onSkipWithoutPhotos ?? onCancel}
                activeOpacity={0.85}
                accessibilityLabel="skip"
                accessibilityRole="button"
              >
                <Text style={cameraStepStyles.skipButtonText}>skip</Text>
              </TouchableOpacity>
            </View>
            <View style={cameraStepStyles.topBarFlashOverlay} pointerEvents="box-none">
              <TouchableOpacity
                style={[cameraStepStyles.flashButton, cameraStepStyles.topBarControlZ]}
                onPress={toggleFlash}
                activeOpacity={0.85}
                accessibilityLabel={flashEnabled ? 'Flash on' : 'Flash off'}
                accessibilityRole="button"
              >
                <Ionicons
                  name={flashEnabled ? 'flash' : 'flash-off'}
                  size={20}
                  color={flashEnabled ? '#FFD700' : '#fff'}
                />
              </TouchableOpacity>
            </View>
          </View>
          <Text style={cameraStepStyles.captureEyebrow} pointerEvents="none">
            Step 2 of 2 · Front photo
          </Text>
          <Text style={cameraStepStyles.captureTitleBelowControls} pointerEvents="none" numberOfLines={2}>
            Capture the moment!
          </Text>
        </View>

        <View style={cameraStepStyles.centerContent} />

        <View style={cameraStepStyles.bottomBar} pointerEvents="box-none">
          <TouchableOpacity
            style={cameraStepStyles.captureButton}
            onPress={capturePhoto}
            activeOpacity={0.7}
            accessibilityLabel="Take front photo"
            accessibilityRole="button"
          >
            <View style={cameraStepStyles.captureButtonInner}>
              <Ionicons name="camera" size={28} color="#8B4513" />
            </View>
          </TouchableOpacity>
        </View>

        {showShutterEffect && <View style={cameraStepStyles.shutterOverlay} />}
      </View>
    </SafeAreaView>
  );
}
